import React, {useMemo} from 'react';
import {DateTime, DateType} from '@cabinetsbycomputer/datetime';
import {JobCost} from 'components/customer/Job/store/jobApi';
import {isEmpty} from 'lodash';
import {formatPrice} from 'shared/helpers';
import {MinimumUsageFee} from 'shared/components/Header/MinimumUsageFee';
import {useJobContext} from 'contexts/JobContext';
import {PartialJob} from 'shared/types/PartialJob';
import {VariationCost} from 'shared/components/Header/VariationCost';
import {useAppContext} from 'contexts/ApplicationContext';
import {Coupon} from 'shared/Coupon';
import {MinimumChargeMoreInfo} from 'components/customer/Confirmation/Checkout/MinimumChargeMoreInfo';

interface LegacyCostBreakdownProps {
    cost: JobCost;
    isLoadingCost: boolean;
    isFetchingCost: boolean;
}

export const LegacyCostBreakdown = ({
    cost,
    isLoadingCost,
    isFetchingCost,
}: LegacyCostBreakdownProps) => {
    const {userProfile} = useAppContext();
    const {job} = useJobContext() as PartialJob;

    const hasFreightCost = useMemo(() => {
        return (
            (cost && cost.freightCost > 0) ||
            (cost &&
                cost.couponDetails &&
                cost.couponDetails.discount_free_shipping)
        );
    }, [cost]);

    const showCoupon =
        userProfile.isCouponAvailable ||
        (cost?.coupon > 0 &&
            cost?.couponDetails &&
            !cost.couponDetails.invalid);

    return (
        <>
            {/* coupon available by manufacturer or job has coupon */}
            {showCoupon ? (
                <li>
                    <Coupon />
                </li>
            ) : null}
            <li>
                <span>
                    Job Subtotal
                    {/** job has coupon and coupon not expired */}
                    {cost &&
                    cost.coupon > 0 &&
                    cost.couponDetails &&
                    (isEmpty(cost.couponDetails.expiry_date) ||
                        (!isEmpty(cost.couponDetails.expiry_date) &&
                            DateTime.now().isBefore(
                                DateTime.parse(
                                    cost.couponDetails.expiry_date,
                                    DateType.ISO
                                ).get(),
                                true
                            )))
                        ? ` (Coupon incl.)`
                        : ``}
                    :
                </span>
                <span>
                    {cost && (!isLoadingCost || !isFetchingCost)
                        ? formatPrice(cost.jobSubtotal, cost)
                        : '...'}
                </span>
            </li>

            {cost?.benchtopMaterialUsage?.length > 0 ||
            cost?.cabinetMaterialUsage?.length > 0 ||
            cost?.cabinetEdgeUsage?.length > 0 ? (
                <MinimumUsageFee />
            ) : null}

            {job.totalProductCount > 0 ? (
                <>
                    {
                        // Minimum charge
                        cost && cost.jobMinChargeFromCustMin > 0 ? (
                            <li>
                                <span style={{display: 'flex'}}>
                                    Minimum Charge Fee:
                                    <MinimumChargeMoreInfo />
                                </span>
                                <span>
                                    {cost && (!isLoadingCost || !isFetchingCost)
                                        ? formatPrice(
                                              cost.jobMinChargeFromCustMin,
                                              cost
                                          )
                                        : '...'}
                                </span>
                            </li>
                        ) : null
                    }
                    <VariationCost />

                    {hasFreightCost ? (
                        <li>
                            <span>
                                Freight{' '}
                                {cost &&
                                cost.couponDetails?.discount_free_shipping
                                    ? '(includes discount)'
                                    : '(minimum cost)'}
                                :
                            </span>
                            <span>
                                {cost && (!isLoadingCost || !isFetchingCost)
                                    ? formatPrice(cost.freightCost, cost)
                                    : '...'}
                            </span>
                        </li>
                    ) : null}

                    <li>
                        <span>{job.countryTaxRateName}</span>
                        <span>
                            {cost && (!isLoadingCost || !isFetchingCost)
                                ? formatPrice(cost.tax, cost)
                                : '...'}
                        </span>
                    </li>
                </>
            ) : null}
            <li className="cost">
                <span>Total Cost:</span>
                <span>
                    {cost && (!isLoadingCost || !isFetchingCost)
                        ? formatPrice(
                              job.totalProductCount > 0 ? cost.totalJobCost : 0,
                              cost
                          )
                        : '...'}
                </span>
            </li>
        </>
    );
};
